import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from './ToastContext';

const AuthContext = createContext(null);

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(() => {
    return localStorage.getItem('isMnqUserSubscribed') === 'true';
  });

  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        setUser({
          id: s.user.id,
          name: s.user.user_metadata?.name || s.user.email?.split('@')[0] || 'User',
          email: s.user.email,
          tier: s.user.user_metadata?.tier || 1,
        });
        setIsLoggedIn(true);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        setUser({
          id: s.user.id,
          name: s.user.user_metadata?.name || s.user.email?.split('@')[0] || 'User',
          email: s.user.email,
          tier: s.user.user_metadata?.tier || 1,
        });
        setIsLoggedIn(true);
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (isSubscribed) {
      localStorage.setItem('isMnqUserSubscribed', 'true');
    } else {
      localStorage.removeItem('isMnqUserSubscribed');
    }
  }, [isSubscribed]);

  const signUp = useCallback(async (email, password) => {
    let utm = {};
    try { utm = JSON.parse(sessionStorage.getItem('anq_utm') || '{}'); } catch {}

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name: email.split('@')[0], tier: 1, ...utm },
        emailRedirectTo: undefined,
      },
    });
    if (error) throw error;
    return data;
  }, []);

  const verifyOtp = useCallback(async (email, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'signup',
    });
    if (error) throw error;
    toast.success('Account verified!');
    return data;
  }, [toast]);

  const resendOtp = useCallback(async (email) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    if (error) throw error;
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    toast.success('Welcome back!');
    return data;
  }, [toast]);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setIsSubscribed(false);
    toast.info('You have been logged out');
  }, [toast]);

  const handleSubscribe = useCallback(() => {
    setIsSubscribed(true);
    localStorage.setItem('isMnqUserSubscribed', 'true');
  }, []);

  const handleUnsubscribe = useCallback(() => {
    setIsSubscribed(false);
    localStorage.removeItem('isMnqUserSubscribed');
  }, []);

  return (
    <AuthContext.Provider value={{
      session,
      user,
      isLoggedIn,
      isSubscribed,
      loading,
      signUp,
      signIn,
      signOut,
      verifyOtp,
      resendOtp,
      handleSubscribe,
      handleUnsubscribe,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
